import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../components/AppShell';
import { presenceApi, isoDate, dayLabel } from '../lib/presenceApi';
import type { DayPresence, StaffAway, StudentAway } from '../lib/presenceApi';
import { StatTile } from '../components/charts';

/**
 * "WHO IS NOT HERE?" — for whatever date the principal picks.
 *
 * Two questions, one screen:
 *   • which teachers are out, and which sections that leaves uncovered
 *   • which children are away — today, next Thursday, or last Monday when a parent rings up
 *
 * ⚠ THE FUTURE-DATE TRAP IS THE WHOLE DESIGN OF THIS PAGE. On a date that has not happened there is
 * no register, so present / absent / late are all zero. Rendering those zeros as attendance figures
 * would tell a principal that 1,380 children are absent tomorrow — and someone who sees that once
 * stops trusting every other number on the screen. `registerTaken` gates every attendance figure,
 * and when it is false the page switches to showing leave APPLICATIONS instead, which is the only
 * thing that legitimately exists for a future date.
 *
 * ⚠ Leave type and reason arrive null unless the caller is in the `hr` policy. That is a server-side
 * redaction, not a UI choice — see PresenceEndpoints. The page renders whatever it is given.
 */
export function PresencePage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState<DayPresence | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // ⚠ A 1,400-child school produces a 110-row "children away" table on an ordinary Tuesday.
  // Correct, and useless — the principal is looking for the unexplained ones, or for one class.
  const [only, setOnly] = useState<'all' | 'absent' | 'leave'>('all');
  const [cls, setCls] = useState<string>('');

  const today = useMemo(() => isoDate(new Date()), []);
  const date = params.get('date') || today;

  // ⚠ Mirrors the backend `hr` policy. If this drifts the HR call 403s, so the load below falls
  // back to the staffroom route rather than showing an error page.
  const canSeeReasons = !!user && (ROLES.hr as readonly string[]).includes(user.role);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    presenceApi
      .day({ date, withReasons: canSeeReasons })
      .catch(() => (canSeeReasons ? presenceApi.day({ date }) : Promise.reject(new Error('no'))))
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [date, canSeeReasons]);

  function pick(next: string) {
    const p = new URLSearchParams(params);
    p.set('date', next);
    setParams(p, { replace: true });
  }

  function shift(days: number) {
    const base = new Date(date + 'T00:00:00');
    base.setDate(base.getDate() + days);
    pick(isoDate(base));
  }

  if (loading && !d) return <p style={{ color: 'var(--ink-3)' }}>Reading the register…</p>;
  if (failed || !d) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the day.</div>;
  }

  const shown = d.studentsAwayList.filter((s) => {
    if (cls && s.classKey !== cls) return false;
    // ⚠ "Unexplained" is the register saying Absent with no application behind it — NOT simply
    // source === 'register', which also covers an approved leave that was marked Excused.
    const unexplained = s.source === 'register' && s.status.startsWith('Absent');
    if (only === 'absent') return unexplained;
    if (only === 'leave') return !unexplained;
    return true;
  });

  const quick: Array<[string, number]> = [
    ['Yesterday', -1],
    ['Today', 0],
    ['Tomorrow', 1],
    ['In a week', 7],
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Who is not here</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '72ch' }}>
          Staff on leave and children away, for any date. Past dates read the register; future dates
          read the leave that has already been applied for.
        </p>
      </div>

      {/* ------------------------------- the date picker ------------------------------- */}
      <div className="tab-strip presence-picker" style={{ marginBottom: 14, borderBottom: 'none' }}>
        <button type="button" className="chip" onClick={() => shift(-1)} aria-label="Previous day">‹</button>
        {quick.map(([label, off]) => {
          const iso = isoDate(new Date(new Date(today + 'T00:00:00').setDate(
            new Date(today + 'T00:00:00').getDate() + off)));
          return (
            <button
              key={label}
              type="button"
              className={'chip' + (iso === date ? ' is-on' : '')}
              onClick={() => pick(iso)}
            >
              {label}
            </button>
          );
        })}
        <button type="button" className="chip" onClick={() => shift(1)} aria-label="Next day">›</button>
        {/* ⚠ width:auto — `.input` is width:100% and inside a .tab-strip it eats the whole row and
            pushes the chips off the scrollable edge. Same trap as the command centre filter bar. */}
        <input
          type="date"
          className="input"
          style={{ width: 'auto' }}
          value={date}
          onChange={(e) => e.target.value && pick(e.target.value)}
        />
        <span style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 600, marginLeft: 4 }}>
          {dayLabel(d.date)}
        </span>
      </div>

      {d.note && (
        <div
          className="card"
          style={{
            padding: '11px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6,
            color: 'var(--ink-2)',
            background: d.isFuture || d.isNonWorkingDay ? 'var(--surface-2)' : 'var(--warn-bg)',
          }}
        >
          ⚠ {d.note}
        </div>
      )}

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        {/* ------------------------------- the tiles ------------------------------- */}
        <div className="presence-tiles" style={{ marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Staff away"
              value={String(d.staffAwayToday)}
              foot={`of ${d.staffTotal} on the roll${d.staffPendingCount > 0
                ? ` · ${d.staffPendingCount} awaiting approval` : ''}`}
            />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Children away"
              value={String(d.studentsOnLeave + d.studentsAbsent)}
              foot={`${d.studentsOnLeave} on leave · ${d.studentsAbsent} unexplained`}
            />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Present"
              /* ⚠ registerTaken, not a count of zero. See the header note. */
              value={d.registerTaken && d.studentPresentPct !== null
                ? `${d.studentPresentPct.toFixed(1)}%` : '—'}
              foot={d.registerTaken
                ? `${d.studentsPresent + d.studentsLate} of ${d.studentsTotal}`
                : 'no register for this date'}
            />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile
              label="Not marked"
              value={d.registerTaken ? String(d.studentsUnmarked) : '—'}
              foot={d.registerTaken && d.studentsUnmarked > 0
                ? <span style={{ color: 'var(--warn)' }}>roll not completed</span>
                : 'every child accounted for'}
            />
          </div>
        </div>

        {/* ------------------------------- staff ------------------------------- */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="viz-title" style={{ marginBottom: 4 }}>
            {/* "Staff away on today" — dayLabel returns a relative word for the three nearest days
                and an absolute date otherwise, so the preposition has to follow it. */}
            Staff away {whenPhrase(d.date)}
          </div>
          <p className="viz-sub">
            {d.staffAwayToday > 0
              ? `${d.staffAwayToday} ${d.staffAwayToday === 1 ? 'person' : 'people'}, `
                + `${d.staffDaysLost} ${d.staffDaysLost === 1 ? 'day' : 'days'} of cover to arrange. `
                + 'A section shown in amber has no class teacher on this date.'
              : 'Nobody is on approved leave for this date.'}
          </p>
          {d.staffAwayList.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>Full attendance in the staffroom.</p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {d.staffAwayList.map((s) => <StaffRow key={s.employeeId} s={s} />)}
            </div>
          )}
        </div>

        {d.staffUpcoming.length > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div className="viz-title" style={{ marginBottom: 4 }}>Coming up</div>
            <p className="viz-sub">
              Approved leave that starts after this date, so cover can be arranged before it is urgent.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="viz-table" style={{ minWidth: 520 }}>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>From</th>
                    <th className="num">Days</th>
                    <th>Classes affected</th>
                  </tr>
                </thead>
                <tbody>
                  {d.staffUpcoming.slice(0, 25).map((s) => (
                    <tr key={s.employeeId + s.fromDate}>
                      <td>
                        {s.fullName}
                        {s.designation && (
                          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6 }}>
                            {s.designation}
                          </span>
                        )}
                      </td>
                      <td>
                        {dayLabel(s.fromDate)}
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6 }}>
                          in {s.startsInDays} day{s.startsInDays === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className="num">{s.isHalfDay ? '½' : s.days}</td>
                      <td>
                        {s.classesUncovered.length === 0
                          ? <span style={{ color: 'var(--ink-3)' }}>—</span>
                          : s.classesUncovered.join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ------------------------------- children away ------------------------------- */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="viz-title" style={{ marginBottom: 4 }}>Children away</div>
          <p className="viz-sub">
            &ldquo;On leave&rdquo; has an application behind it. &ldquo;Absent&rdquo; does not — those are the
            names worth a phone call.
          </p>
          {d.studentsAwayList.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>
              {d.registerTaken ? 'Nobody is away.' : 'No leave has been applied for on this date.'}
            </p>
          ) : (
            <>
              <div className="tab-strip presence-picker"
                   style={{ borderBottom: 'none', marginBottom: 10, gap: 6 }}>
                {([['all', 'Everyone'], ['absent', 'Unexplained only'], ['leave', 'On leave only']] as const)
                  .map(([k, label]) => (
                    <button key={k} type="button"
                            className={'chip' + (only === k ? ' is-on' : '')}
                            onClick={() => setOnly(k)}>
                      {label}
                    </button>
                  ))}
                <select className="input" style={{ width: 'auto' }}
                        value={cls} onChange={(e) => setCls(e.target.value)}>
                  <option value="">All classes</option>
                  {d.classes.map((c) => (
                    <option key={c.classKey} value={c.classKey}>{c.classLabel}</option>
                  ))}
                </select>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="viz-table" style={{ minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Status</th>
                      <th>Reason given</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((s) => <StudentRow key={s.studentId} s={s} />)}
                  </tbody>
                </table>
              </div>
              {shown.length === 0 && (
                <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '10px 0 0' }}>
                  Nothing matches that filter — which is usually good news.
                </p>
              )}
              {shown.length < d.studentsAwayList.length && shown.length > 0 && (
                <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '10px 0 0' }}>
                  Showing {shown.length} of {d.studentsAwayList.length}.
                </p>
              )}
            </>
          )}
        </div>

        {/* ------------------------------- by class -------------------------------
            ⚠ ONLY WHEN A REGISTER EXISTS. Rendering this table on a future date produced a full
            page of amber "52 not marked" and "0%" for every section in the school — technically
            accurate, and it read as though the product was broken. On a date with no register the
            only honest breakdown is which classes have leave booked, which is the block below. */}
        {d.classes.length > 0 && d.registerTaken && (
          <div className="card" style={{ padding: 16 }}>
            <div className="viz-title" style={{ marginBottom: 4 }}>Class by class</div>
            <p className="viz-sub">
              &ldquo;Not marked&rdquo; is a roll that was never completed, which is a different problem from
              a child being absent — and the one the office can fix the same morning.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="viz-table" style={{ minWidth: 560 }}>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th className="num">Strength</th>
                    <th className="num">Present</th>
                    <th className="num">Leave</th>
                    <th className="num">Absent</th>
                    <th className="num">Late</th>
                    <th className="num">Not marked</th>
                    <th className="num">%</th>
                  </tr>
                </thead>
                <tbody>
                  {d.classes.map((c) => (
                    <tr key={c.classKey}>
                      <td>{c.classLabel}</td>
                      <td className="num">{c.strength}</td>
                      <td className="num">{c.present}</td>
                      <td className="num">{c.onLeave || ''}</td>
                      <td className="num" style={{ color: c.absent > 0 ? 'var(--danger)' : undefined }}>
                        {c.absent || ''}
                      </td>
                      <td className="num">{c.late || ''}</td>
                      <td className="num" style={{ color: c.unmarked > 0 ? 'var(--warn)' : undefined }}>
                        {c.unmarked || ''}
                      </td>
                      <td className="num" style={{ fontWeight: 650 }}>
                        {/* ⚠ "—" not "0%". Nothing marked is not nobody present. */}
                        {c.presentPct === null ? '—' : `${c.presentPct.toFixed(0)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!d.registerTaken && d.classes.some((c) => c.onLeave > 0) && (
          <div className="card" style={{ padding: 16 }}>
            <div className="viz-title" style={{ marginBottom: 4 }}>Leave booked, class by class</div>
            <p className="viz-sub">
              No register exists for this date, so there is nothing to report on attendance. These
              are the sections that will be a child or two short.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {d.classes.filter((c) => c.onLeave > 0)
                .sort((a, b) => b.onLeave - a.onLeave)
                .map((c) => (
                  <span key={c.classKey} className="pill pill-muted" style={{ fontSize: 12 }}>
                    {c.classLabel} · {c.onLeave} away
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** "today" / "yesterday" / "on Mon 17 Aug" — reads as English either way. */
function whenPhrase(iso: string): string {
  const label = dayLabel(iso);
  return ['Today', 'Tomorrow', 'Yesterday'].includes(label) ? label.toLowerCase() : `on ${label}`;
}

function StaffRow({ s }: { s: StaffAway }) {
  const uncovered = s.classesUncovered.length > 0;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px',
        borderRadius: 8, background: uncovered ? 'var(--warn-bg)' : 'var(--surface-2)',
      }}
    >
      <span style={{ flex: '1 1 200px', minWidth: 0 }}>
        <strong style={{ fontSize: 14 }}>{s.fullName}</strong>
        {s.designation && (
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)', marginLeft: 8 }}>
            {s.designation}{s.department ? ` · ${s.department}` : ''}
          </span>
        )}
        {/* Only present on the HR view — the staffroom view gets null and renders nothing. */}
        {s.reason && (
          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
            {s.reason}
          </span>
        )}
      </span>
      {s.leaveType && <span className="pill" style={{ fontSize: 10 }}>{s.leaveType}</span>}
      <span style={{ fontSize: 12, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>
        {/* ⚠ A half day is a half day. Rounding it to "1 day" overstates the cover needed. */}
        {s.isHalfDay ? 'half day' : `${s.days} day${s.days === 1 ? '' : 's'}`}
        {s.isHalfDay ? '' : s.daysRemaining > 0 ? ` · back in ${s.daysRemaining + 1}` : ' · last day'}
      </span>
      {uncovered && (
        <span className="pill pill-warn" style={{ fontSize: 10 }} title="No class teacher for this section today">
          {s.classesUncovered.join(', ')} uncovered
        </span>
      )}
    </div>
  );
}

function StudentRow({ s }: { s: StudentAway }) {
  const unexplained = s.source === 'register' && s.status.startsWith('Absent');
  return (
    <tr>
      <td>
        {s.fullName}
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 6 }}>
          {s.admissionNumber}
        </span>
      </td>
      <td>{s.classLabel}</td>
      <td>
        <span
          className={'pill ' + (unexplained ? 'pill-danger' : s.source === 'application' ? 'pill-warn' : '')}
          style={{ fontSize: 10 }}
        >
          {s.status}
        </span>
        {s.fromDate && s.toDate && s.fromDate !== s.toDate && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6 }}>
            {dayLabel(s.fromDate)} → {dayLabel(s.toDate)}
          </span>
        )}
      </td>
      <td style={{ color: s.reason ? undefined : 'var(--ink-3)' }}>{s.reason ?? '—'}</td>
    </tr>
  );
}
